import {
	getEndorsementRenewalExposureAddedPerilForm,
	getEndorsementRenewalExposureExistingPerilForm,
} from '.';
import { HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import {
	getCharacteristic,
	getExposureConfig,
	isExposureActive,
} from '../../../util/util';
import {
	consolidateFieldGroupsByLocator,
	getUpdatedExposureObj,
	removePendingRemoveFieldGroupsLocatorsFromFieldValues,
} from '../../policy/util_sharedEndorsementAndRenewal';
import { EndorsementFormTransformer, FormBuilder, FormShape } from '../types';

const getEndorsementExposuresForm = (props: EndorsementFormTransformer) => {
	const exposureForm = {} as FormShape<FormBuilder>;
	const { endorsementData, authData } = props;
	const { policy, productConfig, endorsementUpdateObj } = endorsementData;
	const { addExposures } = endorsementUpdateObj;
	const startTimestamp = endorsementUpdateObj.startTimestamp;
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const existingExposures = policy.exposures;

	for (const [, exposure] of existingExposures.entries()) {
		const isActive = isExposureActive(
			exposure,
			policy,
			endorsementUpdateObj.startTimestamp,
		);

		if (isActive) {
			const exposureUpdateObj = getUpdatedExposureObj(
				endorsementUpdateObj,
				exposure.locator,
			);

			const currentExposureConfiguration = getExposureConfig(
				productConfig,
				exposure,
			);
			const currentExposureCharacteristics = getCharacteristic(
				exposure.characteristics,
				endorsementUpdateObj.startTimestamp,
			);

			const originalFieldValues = currentExposureCharacteristics.fieldValues;
			const pendingFieldValues = exposureUpdateObj.fieldValues
				? { ...exposureUpdateObj.fieldValues }
				: {};
			const addFieldGroups = exposureUpdateObj.addFieldGroups
				? [...exposureUpdateObj.addFieldGroups]
				: [];
			const removeFieldGroups = exposureUpdateObj.removeFieldGroups
				? [...exposureUpdateObj.removeFieldGroups]
				: [];
			const pendingRemoveFieldGroups = exposureUpdateObj.removeFieldGroups
				? [...exposureUpdateObj.removeFieldGroups]
				: [];
			const pendingUpdateFieldGroups = exposureUpdateObj.updateFieldGroups
				? [...exposureUpdateObj.updateFieldGroups]
				: [];
			const fieldGroupsByLocator = consolidateFieldGroupsByLocator(
				{ ...currentExposureCharacteristics.fieldGroupsByLocator },
				pendingUpdateFieldGroups,
				pendingRemoveFieldGroups,
			);
			const values = removePendingRemoveFieldGroupsLocatorsFromFieldValues(
				pendingRemoveFieldGroups,
				{ ...originalFieldValues, ...pendingFieldValues },
			);

			removeFieldGroups.forEach((locator) => {
				delete fieldGroupsByLocator[locator];
			});

			exposureForm[exposure.locator] = {
				externalDataCalls: currentExposureConfiguration.externalDataCalls,
				mediaByLocator: currentExposureCharacteristics.mediaByLocator,
				fields: currentExposureConfiguration.fields,
				fieldGroupsByLocator,
				values,
				addFieldGroups,
			} as FormShape<FormBuilder>;

			exposure.perils.forEach((selectedPeril) => {
				const perilLocator = selectedPeril.locator;
				(exposureForm[exposure.locator] as FormShape<FormBuilder>)[
					perilLocator
				] = getEndorsementRenewalExposureExistingPerilForm(
					selectedPeril,
					exposure,
					exposureUpdateObj,
					currentExposureConfiguration,
					policy.currency,
					startTimestamp,
					hidePerilLimitsEnabled,
				);
			});

			exposureUpdateObj.addPerils.forEach((selectedPeril, perilIndex) => {
				(exposureForm[exposure.locator] as FormShape<FormBuilder>)[
					`peril-${perilIndex}`
				] = getEndorsementRenewalExposureAddedPerilForm(
					selectedPeril,
					exposure,
					policy.currency,
					productConfig,
					hidePerilLimitsEnabled,
				);
			});
		}
	}
	if (addExposures) {
		addExposures.forEach((exposure, index) => {
			const exposureUpdateObj = getUpdatedExposureObj(
				endorsementUpdateObj,
				exposure.locator,
			);
			const exposureName = exposure.exposureName;
			const policyConfig = productConfig.policyConfiguration;
			const currentExposureConfiguration = policyConfig.exposures.find(
				(exposure) => exposure.name === exposureName,
			);

			const originalFieldValues = exposure.fieldValues;
			const pendingFieldValues = exposureUpdateObj.fieldValues
				? { ...exposureUpdateObj.fieldValues }
				: {};
			const addFieldGroups = exposureUpdateObj.addFieldGroups
				? [...exposureUpdateObj.addFieldGroups]
				: [];
			const removeFieldGroups = exposureUpdateObj.removeFieldGroups
				? [...exposureUpdateObj.removeFieldGroups]
				: [];
			const pendingRemoveFieldGroups = exposureUpdateObj.removeFieldGroups
				? [...exposureUpdateObj.removeFieldGroups]
				: [];
			const pendingUpdateFieldGroups = exposureUpdateObj.updateFieldGroups
				? [...exposureUpdateObj.updateFieldGroups]
				: [];
			const fieldGroupsByLocator = consolidateFieldGroupsByLocator(
				{},
				pendingUpdateFieldGroups,
				pendingRemoveFieldGroups,
			);
			const values = removePendingRemoveFieldGroupsLocatorsFromFieldValues(
				pendingRemoveFieldGroups,
				{ ...originalFieldValues, ...pendingFieldValues },
			);

			removeFieldGroups.forEach((locator) => {
				delete fieldGroupsByLocator[locator];
			});

			exposureForm[`exposure-${index}`] = {
				externalDataCalls: currentExposureConfiguration.externalDataCalls,
				fields: currentExposureConfiguration.fields,
				fieldGroupsByLocator,
				values,
				addFieldGroups,
				fieldGroups: exposure.fieldGroups,
			} as FormShape<FormBuilder>;

			exposure.perils.forEach((selectedPeril, perilIndex) => {
				(exposureForm[`exposure-${index}`] as FormShape<FormBuilder>)[
					`peril-${perilIndex}`
				] = getEndorsementRenewalExposureAddedPerilForm(
					selectedPeril,
					exposure,
					policy.currency,
					productConfig,
					hidePerilLimitsEnabled,
				);
			});
		});
	}

	return exposureForm as FormShape<FormBuilder>;
};

export default getEndorsementExposuresForm;
